import { CheckDescriptors } from '../types';

export const ORGANIZATION_SETTINGS = [
    {
        id: 'organization.basic.name',
        area: 'organization.basic',
        label: 'Organization Name',
    },
    {
        id: 'organization.basic.logo',
        area: 'organization.basic',
        label: 'Organization Logo',
        description: "Shown as this organization's icon in the admin nav bar's organization switcher.",
    },
    {
        id: 'organization.basic.country',
        area: 'organization.basic',
        label: 'Country',
    },
    {
        id: 'organization.basic.defaultCatalog',
        area: 'organization.basic',
        label: 'Default Catalog',
        description: 'The catalog used when a request does not name one.',
    },
    {
        id: 'organization.basic.punchoutCurrency',
        area: 'organization.basic',
        label: 'Punchout Currency',
        description: 'The currency code included in the cXML PunchOut order message sent to procurement systems.',
    },
    {
        id: 'organization.advanced.enableDynamicProductListings',
        area: 'organization.advanced',
        label: 'Use Dynamic Product Listings',
        description:
            'Turns on scheduled dynamic product listing updates; the schedule and manual run control below only appear when this is on.',
    },
    {
        id: 'organization.advanced.dynamicProductListingSchedule',
        area: 'organization.advanced',
        label: 'Dynamic Product Update Schedule',
        description:
            'Only products resolved within the last 24 hours are included in listing results on the storefront.',
        condition: 'Only appears when Use Dynamic Product Listings is on.',
        dependsOn: 'organization.advanced.enableDynamicProductListings',
    },
    {
        id: 'organization.advanced.enableSimpleTaxCalculation',
        area: 'organization.advanced',
        label: 'Use Simple Tax Calculation For Storefront',
        description:
            'Applies one flat tax rate per storefront instead of calculating tax by customer location, product type, or shipping address.',
    },
    // ---------------------------------------------------------------- procurement
    {
        id: 'procurement.settings.enableManualItemProcurement',
        area: 'procurement',
        label: 'Enable Manual Item Procurement',
        description: 'Lets a procurement order include items typed in manually rather than only catalog products.',
    },

    // ---------------------------------------------------------------- my user
    {
        id: 'myUser.profile.displayName',
        area: 'myUser',
        label: 'Display Name',
    },
    {
        id: 'myUser.profile.givenName',
        area: 'myUser',
        label: 'Given Name',
    },
    {
        id: 'myUser.profile.surname',
        area: 'myUser',
        label: 'Surname',
    },
    {
        id: 'myUser.profile.jobTitle',
        area: 'myUser',
        label: 'Job Title',
    },
    {
        id: 'myUser.profile.department',
        area: 'myUser',
        label: 'Department',
    },
    {
        id: 'myUser.profile.companyName',
        area: 'myUser',
        label: 'Company Name',
    },
    {
        id: 'myUser.profile.officePhone',
        area: 'myUser',
        label: 'Office Phone',
    },
    {
        id: 'myUser.profile.mobilePhone',
        area: 'myUser',
        label: 'Mobile Phone',
    },
    {
        id: 'myUser.profile.preferredLanguage',
        area: 'myUser',
        label: 'Preferred Language',
    },
    {
        id: 'myUser.profile.officeLocation',
        area: 'myUser',
        label: 'Office Location',
    },
    {
        id: 'myUser.address.streetAddress',
        area: 'myUser',
        label: 'Street Address',
    },
    {
        id: 'myUser.address.city',
        area: 'myUser',
        label: 'City',
    },
    {
        id: 'myUser.address.state',
        area: 'myUser',
        label: 'State',
    },
    {
        id: 'myUser.address.country',
        area: 'myUser',
        label: 'Country',
    },
    {
        id: 'myUser.address.postalCode',
        area: 'myUser',
        label: 'Postal Code',
    },

    // ---------------------------------------------------------------- vendors
    {
        id: 'vendors.details.sourceVendor',
        area: 'vendors.details',
        label: 'Vendor Name',
        description:
            'The vendor name exactly as it appears on products. Other settings are matched to it, so it must be spelled the same way.',
    },
    {
        id: 'vendors.details.vendorDisplayName',
        area: 'vendors.details',
        label: 'Display Name',
        description:
            'Shown instead of the raw vendor name wherever the vendor appears to users. Falls back to the vendor name when blank.',
    },
    {
        id: 'vendors.details.enabled',
        area: 'vendors.details',
        label: 'Enabled',
    },
    {
        id: 'vendors.details.punchoutVendorName',
        area: 'vendors.details',
        label: 'Punchout Name',
        description: "Replaces the vendor's name on cXML punchout order line items. Falls back to the vendor name when blank.",
    },

    // ---------------------------------------------------------------- webhooks
    {
        id: 'webhooks.details.type',
        area: 'webhooks.details',
        label: 'Webhook Type',
        description: 'Fixed once the webhook is saved; a different event is a different webhook.',
    },
    {
        id: 'webhooks.details.name',
        area: 'webhooks.details',
        label: 'Name',
        description:
            'Prefilled from the selected webhook type until edited by hand. After that, changing the type no longer overwrites it.',
    },
    {
        id: 'webhooks.details.url',
        area: 'webhooks.details',
        label: 'URL',
        description: "The endpoint the webhook's JSON payload is POSTed to when its event fires.",
    },
    {
        id: 'webhooks.details.headers',
        area: 'webhooks.details',
        label: 'Custom Headers',
        description: 'HTTP headers sent with every call of this webhook. Values can be plain text, tokens or a mix; mark a header Secret to hide its value after saving.',
    },
    {
        id: 'webhooks.details.useCustomPayload',
        area: 'webhooks.details',
        label: 'Use Custom Payload',
        description: 'Off sends the default payload for this event; on sends the JSON entered in Request JSON below.',
    },
    {
        id: 'webhooks.details.requestBody',
        area: 'webhooks.details',
        label: 'Request JSON',
        description: 'Only sent for types that substitute tokens.',
        condition: 'Only appears when Use Custom Payload is on.',
        dependsOn: 'webhooks.details.useCustomPayload',
    },
    {
        id: 'webhooks.details.globalEnabled',
        area: 'webhooks.details',
        label: 'Enabled Globally',
        description:
            'When on, the webhook fires for every storefront or extension of its type instead of only the ones selected below.',
        condition: 'Only appears when Webhook Type is not Dynamic Product Listing Export.',
        dependsOn: 'webhooks.details.type',
    },
    {
        id: 'webhooks.details.vendor',
        area: 'webhooks.details',
        label: 'Vendor',
        description: 'The vendor whose product-list export fires this webhook.',
        condition: 'Only appears when Webhook Type is Dynamic Product Listing Export.',
        dependsOn: 'webhooks.details.type',
    },
    // ---------------------------------------------------------------- companies
    {
        id: 'contacts.company.companyName',
        area: 'contacts.company',
        label: 'Company Name',
    },
    {
        id: 'contacts.company.companyIdentifier',
        area: 'contacts.company',
        label: 'Company Identifier',
        description: 'The company’s id in your own systems; matched on during import.',
    },
    {
        id: 'contacts.company.createDate',
        area: 'contacts.company',
        label: 'Create Date',
        description: 'Read only.',
    },
    {
        id: 'contacts.company.lastUpdated',
        area: 'contacts.company',
        label: 'Last Updated',
        description: 'Read only.',
    },
    {
        id: 'contacts.company.website',
        area: 'contacts.company',
        label: 'Website',
    },
    {
        id: 'contacts.company.address1',
        area: 'contacts.company',
        label: 'Address 1',
        description:
            "Copied onto a procurement order's end user, ship-to, or bill-to address when a contact from this company is selected.",
    },
    {
        id: 'contacts.company.address2',
        area: 'contacts.company',
        label: 'Address 2',
        description:
            "Copied onto a procurement order's end user, ship-to, or bill-to address when a contact from this company is selected.",
    },
    {
        id: 'contacts.company.address3',
        area: 'contacts.company',
        label: 'Address 3',
        description:
            "Copied onto a procurement order's end user, ship-to, or bill-to address when a contact from this company is selected.",
    },
    {
        id: 'contacts.company.city',
        area: 'contacts.company',
        label: 'City',
        description:
            "Copied onto a procurement order's end user, ship-to, or bill-to address when a contact from this company is selected.",
    },
    {
        id: 'contacts.company.stateTerritory',
        area: 'contacts.company',
        label: 'State',
        description:
            "Copied onto a procurement order's end user, ship-to, or bill-to address when a contact from this company is selected.",
    },
    {
        id: 'contacts.company.country',
        area: 'contacts.company',
        label: 'Country',
        description:
            "Copied onto a procurement order's end user, ship-to, or bill-to address when a contact from this company is selected.",
    },
    {
        id: 'contacts.company.postalCode',
        area: 'contacts.company',
        label: 'Postal Code',
        description:
            "Copied onto a procurement order's end user, ship-to, or bill-to address when a contact from this company is selected.",
    },
    {
        id: 'contacts.company.contactFirstName',
        area: 'contacts.company',
        label: 'Procurement Contact First Name',
        description: "Used as this company's default contact when a workspace request has no specific contact selected.",
    },
    {
        id: 'contacts.company.contactLastName',
        area: 'contacts.company',
        label: 'Procurement Contact Last Name',
        description: "Used as this company's default contact when a workspace request has no specific contact selected.",
    },
    {
        id: 'contacts.company.contactEmail',
        area: 'contacts.company',
        label: 'Procurement Contact Email',
        description: "Used as this company's default contact when a workspace request has no specific contact selected.",
    },
    {
        id: 'contacts.company.contactPhone',
        area: 'contacts.company',
        label: 'Procurement Contact Phone',
        description: "Used as this company's default contact when a workspace request has no specific contact selected.",
    },
    {
        id: 'contacts.company.primaryContact',
        area: 'contacts.company',
        label: 'Primary Contact',
    },
    {
        id: 'contacts.company.syncOverride',
        area: 'contacts.company',
        label: 'Sync this company from CRM',
        description: 'Overrides the org-wide CRM sync setting for this one company.',
    },
    {
        id: 'contacts.company.syncContacts',
        area: 'contacts.company',
        label: 'Sync contacts',
        description: 'Inherits from the company setting unless set explicitly.',
    },
    {
        id: 'contacts.company.syncLocations',
        area: 'contacts.company',
        label: 'Sync locations',
        description: 'Inherits from the company setting unless set explicitly.',
    },
    // ---------------------------------------------------------------- contacts
    {
        id: 'contacts.contact.firstName',
        area: 'contacts.contact',
        label: 'First Name',
    },
    {
        id: 'contacts.contact.lastName',
        area: 'contacts.contact',
        label: 'Last Name',
    },
    {
        id: 'contacts.contact.email',
        area: 'contacts.contact',
        label: 'Email',
    },
    {
        id: 'contacts.contact.phone',
        area: 'contacts.contact',
        label: 'Phone',
    },
    {
        id: 'contacts.contact.defaultLocation',
        area: 'contacts.contact',
        label: 'Default Location',
        description: 'Requires a company; the list is that company’s locations.',
    },
    {
        id: 'contacts.contact.procurementShippingContact',
        area: 'contacts.contact',
        label: 'Procurement Shipping Contact',
        description: 'Whether this contact is selectable as a ship-to on non-dropship procurement orders.',
    },
    {
        id: 'contacts.contact.procurementBillingContact',
        area: 'contacts.contact',
        label: 'Procurement Billing Contact',
        description: 'Whether this contact is selectable as a bill-to on procurement orders.',
    },
    {
        id: 'contacts.contact.externalId',
        area: 'contacts.contact',
        label: 'Default External ID',
        description: "The contact's id in the connected CRM/PSA system, used to match this contact during sync.",
    },
    {
        id: 'contacts.contact.externalIdType',
        area: 'contacts.contact',
        label: 'Default External ID Type',
        description: 'Which connected CRM/PSA system the default external id belongs to.',
    },
    /*
     * The fixed fields of the install form. Everything below them is schema-driven per extension
     * and carries a dynamic id instead (extension.<source>.<field>), so it needs no entry here.
     * The single-extension form and the folio form share these ids: same settings, and only one
     * of the two forms is ever mounted.
     */
    {
        id: 'extensions.configuration.extensionLevel',
        area: 'extensions.configuration',
        label: 'Extension Level',
        description: 'Whether the extension is installed for the organization or for one user. Fixed after install.',
    },
    {
        id: 'extensions.configuration.nickname',
        area: 'extensions.configuration',
        label: 'Nickname',
        description: 'What this installed extension is called in the list, when one extension is installed more than once. Not available on folio extensions.',
        // Only the single-extension install form has this field; the folio form has no Nickname.
        instanceFilter: '!Folio',
    },
    {
        id: 'extensions.configuration.targetCatalog',
        area: 'extensions.configuration',
        label: 'Target Catalog',
        // Category is immutable and unregistered as a setting, so there is no dependsOn to name.
        condition: 'Only appears for extensions that write products into a catalog.',
    },
    {
        id: 'extensions.configuration.vendor',
        area: 'extensions.configuration',
        label: 'Vendor',
        condition: 'Only appears for vendor extensions.',
    },
    {
        id: 'extensions.configuration.createProducts',
        area: 'extensions.configuration',
        label: 'Create Products',
        description: 'Whether the import creates products it cannot match, or only updates existing ones.',
        condition: 'Only appears for Import extensions.',
    },
] as const;

export type _OrganizationSettingsAreValid = CheckDescriptors<typeof ORGANIZATION_SETTINGS>;
